import { useRef, useState } from "react";
import { FileText, Upload } from "lucide-react";

import {
  useAddProductDocument,
  useDocumentIndexStatus,
  useIndexDocument,
  useProductMedia,
  useUpdateDocumentMetadata,
  type DocumentMetadataInput,
} from "@/api/queries";
import type { DocumentIndexState, Media, MediaType, Product, ValueSource } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { StatusBadge, type BadgeTone } from "@/components/StatusBadge";
import { EmptyState } from "@/components/States";
import { formatDate } from "@/lib/format";

/**
 * Les documents du produit (TICKET PM-03 §10).
 *
 * Une fiche technique sans sa révision est une fiche dont personne ne peut dire
 * si elle décrit encore l'article dans le bac. C'est pour ça que la révision,
 * la langue et la date d'entrée en vigueur sont saisissables ici plutôt que
 * devinées : une révision B et une révision C d'un même disjoncteur peuvent
 * différer sur le couple de serrage, et c'est le genre d'écart qui se paie sur
 * un chantier.
 *
 * Les photos ne sont pas listées ici : elles ont leur propre place, et une
 * photo de bac n'a ni révision ni date d'entrée en vigueur.
 */

const DOCUMENT_TYPES: { value: MediaType; label: string }[] = [
  { value: "DATASHEET", label: "Fiche technique" },
  { value: "MANUAL", label: "Manuel" },
  { value: "WIRING_DIAGRAM", label: "Schéma de câblage" },
  { value: "DRAWING", label: "Plan" },
  { value: "CERTIFICATE", label: "Certificat" },
  { value: "SDS", label: "Fiche de sécurité" },
  { value: "CALIBRATION_PROCEDURE", label: "Procédure d'étalonnage" },
  { value: "WARRANTY", label: "Garantie" },
  { value: "INVOICE", label: "Facture" },
  { value: "OTHER", label: "Autre" },
];

const DOCUMENT_KINDS = new Set<MediaType>(DOCUMENT_TYPES.map((entry) => entry.value));

/**
 * Les quatre états de PM-03, en français (V1.5-09 §30).
 *
 * Les valeurs de l'API restent PENDING / INDEXED / FAILED / STALE — inventer un
 * cinquième état côté écran rendrait l'écran et l'API incomparables, ce qui est
 * exactement le défaut que le contrat d'enums a attrapé en écrivant l'ADR 0014.
 */
const INDEX_LABELS: Record<DocumentIndexState, { label: string; tone: BadgeTone }> = {
  PENDING: { label: "À indexer", tone: "unknown" },
  INDEXED: { label: "Indexé", tone: "available" },
  FAILED: { label: "Échec d'indexation", tone: "out" },
  STALE: { label: "Obsolète", tone: "warning" },
};

/** Les types de fichier que la recherche documentaire sait lire. */
const INDEXABLE_MIME = new Set(["application/pdf", "text/plain", "text/csv"]);

const SOURCES: { value: ValueSource; label: string }[] = [
  { value: "MANUFACTURER", label: "Fabricant" },
  { value: "SUPPLIER", label: "Fournisseur" },
  { value: "WEB", label: "Web" },
  { value: "HUMAN", label: "Fourni par un collègue" },
  { value: "IMPORT", label: "Import" },
];

function typeLabel(type: MediaType) {
  return DOCUMENT_TYPES.find((entry) => entry.value === type)?.label ?? type;
}

export function ProductDocumentsCard({ product }: { product: Product }) {
  const { data: media } = useProductMedia(product.id);
  const addDocument = useAddProductDocument(product.id);
  const fileInput = useRef<HTMLInputElement>(null);
  const [pendingType, setPendingType] = useState<MediaType>("DATASHEET");
  const [editing, setEditing] = useState<Uuid | null>(null);

  const documents = (media ?? []).filter((entry) => DOCUMENT_KINDS.has(entry.media_type));

  return (
    <Card>
      <CardHeader
        title="Documents"
        subtitle="Fiches, manuels, schémas — avec leur révision, pour savoir laquelle est encore bonne."
      />
      <CardBody className="flex flex-col gap-3">
        {/* Le dépôt vit dans le corps de la carte, pas dans son en-tête : un
            menu et un bouton à droite d'un titre écrasaient le titre à « D… »
            sur une colonne étroite. */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={pendingType}
            onChange={(event) => setPendingType(event.target.value as MediaType)}
            className="rounded-md border border-border bg-surface px-2 py-1 text-[12px]"
            aria-label="Type du document à déposer"
          >
            {DOCUMENT_TYPES.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={addDocument.isPending}
            className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[12px] hover:bg-surface-raised disabled:opacity-50"
          >
            <Upload className="size-3.5" aria-hidden />
            Déposer
          </button>
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) addDocument.mutate({ file, mediaType: pendingType });
              event.target.value = "";
            }}
          />
        </div>

        {documents.length === 0 ? (
          <EmptyState
            icon={<FileText className="size-5" aria-hidden />}
            title="Aucun document"
            description="La fiche technique et le manuel se déposent ici, avec leur révision et leur langue."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {documents.map((document) =>
              editing === document.id ? (
                <DocumentForm
                  key={document.id}
                  productId={product.id}
                  document={document}
                  onDone={() => setEditing(null)}
                />
              ) : (
                <DocumentRow
                  key={document.id}
                  productId={product.id}
                  document={document}
                  onEdit={() => setEditing(document.id)}
                />
              ),
            )}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

type Uuid = string;

function DocumentRow({
  productId,
  document,
  onEdit,
}: {
  productId: Uuid;
  document: Media;
  onEdit: () => void;
}) {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2">
      <div className="min-w-0">
        <p className="truncate text-[13px] text-ink">
          {document.original_filename ?? typeLabel(document.media_type)}
        </p>
        <p className="text-[12px] text-ink-subtle">
          {[
            typeLabel(document.media_type),
            document.revision ? `rév. ${document.revision}` : null,
            document.language?.toUpperCase() ?? null,
            document.effective_date ? formatDate(document.effective_date) : null,
            document.document_manufacturer,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <IndexLine productId={productId} document={document} />
      </div>
      <div className="flex items-center gap-2">
        {/* Une fiche sans révision n'est pas une fiche fausse : c'est une fiche
            dont on ne sait pas si elle est à jour, et le dire est plus utile
            que de laisser un blanc. */}
        {!document.revision && <StatusBadge tone="warning">révision inconnue</StatusBadge>}
        <button
          type="button"
          onClick={onEdit}
          className="text-[12px] text-ink-subtle hover:text-ink"
        >
          Modifier
        </button>
      </div>
    </li>
  );
}

/**
 * L'état de la recherche documentaire pour ce fichier (V1.5-09 §30).
 *
 * Un document déposé n'est pas cherchable tant qu'il n'est pas indexé, et rien
 * à l'écran ne le disait : quelqu'un déposait un manuel, posait une question
 * dessus, et concluait que l'assistant ne sait pas lire. La ligne ci-dessous
 * est la réponse à « pourquoi il ne trouve rien ».
 */
function IndexLine({ productId, document }: { productId: Uuid; document: Media }) {
  const { can } = useAuth();
  const mayIndex = can("ADMIN", "MANAGER");
  const { data: status } = useDocumentIndexStatus(document.id, {
    // L'indexation avance en arrière-plan : un état figé jusqu'au prochain
    // rechargement laisserait croire qu'elle est bloquée.
    refetchInterval: (query) =>
      query.state.data?.index_state === "PENDING" ? 4000 : false,
  });
  const index = useIndexDocument(productId);

  if (!INDEXABLE_MIME.has(document.mime_type)) return null;

  const state = status?.index_state ?? document.index_state ?? "PENDING";
  const { label, tone } = INDEX_LABELS[state];
  const done = state === "INDEXED" || state === "STALE";

  return (
    <p className="flex flex-wrap items-center gap-2 pt-1 text-[12px] text-ink-subtle">
      <StatusBadge tone={tone}>{label}</StatusBadge>
      {done && status ? (
        <span>
          {status.chunk_count} extrait{status.chunk_count > 1 ? "s" : ""}
          {status.page_count > 0 ? ` · ${status.page_count} page(s)` : ""}
          {/* Le mode de lecture est dit : un texte reconnu optiquement peut
              porter des erreurs, et une citation qui en vient se relit sur
              l'original. */}
          {status.extractor === "tesseract" ? " · lu optiquement" : ""}
          {status.truncated ? " · document tronqué" : ""}
        </span>
      ) : null}
      {status?.index_error && <span className="text-status-out">{status.index_error}</span>}
      {mayIndex && (
        <button
          type="button"
          disabled={index.isPending}
          onClick={() => index.mutate({ mediaId: document.id, force: state !== "PENDING" })}
          className="rounded-md border border-border px-1.5 py-0.5 hover:bg-surface-raised disabled:opacity-50"
        >
          {state === "FAILED" ? "Réessayer" : state === "PENDING" ? "Indexer" : "Réindexer"}
        </button>
      )}
    </p>
  );
}

function DocumentForm({
  productId,
  document,
  onDone,
}: {
  productId: Uuid;
  document: Media;
  onDone: () => void;
}) {
  const update = useUpdateDocumentMetadata(productId);
  const [form, setForm] = useState<DocumentMetadataInput>({
    media_type: document.media_type,
    language: document.language,
    revision: document.revision,
    document_number: document.document_number,
    effective_date: document.effective_date,
    source: document.source,
    document_manufacturer: document.document_manufacturer,
  });

  const set = <K extends keyof DocumentMetadataInput>(key: K, value: DocumentMetadataInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <li className="py-2">
      <form
        className="grid grid-cols-2 gap-2 text-[12px]"
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate({ mediaId: document.id, changes: form }, { onSuccess: onDone });
        }}
      >
        <Field label="Type">
          <select
            value={form.media_type}
            onChange={(event) => set("media_type", event.target.value as MediaType)}
            className="w-full rounded-md border border-border bg-surface px-2 py-1"
          >
            {DOCUMENT_TYPES.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Révision">
          <input
            value={form.revision ?? ""}
            onChange={(event) => set("revision", event.target.value || null)}
            placeholder="C"
            className="w-full rounded-md border border-border bg-surface px-2 py-1"
          />
        </Field>
        <Field label="Langue">
          <input
            value={form.language ?? ""}
            onChange={(event) => set("language", event.target.value || null)}
            placeholder="fr"
            maxLength={8}
            className="w-full rounded-md border border-border bg-surface px-2 py-1"
          />
        </Field>
        <Field label="Numéro de document">
          <input
            value={form.document_number ?? ""}
            onChange={(event) => set("document_number", event.target.value || null)}
            className="w-full rounded-md border border-border bg-surface px-2 py-1"
          />
        </Field>
        <Field label="En vigueur le">
          <input
            type="date"
            value={form.effective_date ?? ""}
            onChange={(event) => set("effective_date", event.target.value || null)}
            className="w-full rounded-md border border-border bg-surface px-2 py-1"
          />
        </Field>
        <Field label="Provenance">
          <select
            value={form.source ?? ""}
            onChange={(event) => set("source", (event.target.value || null) as ValueSource | null)}
            className="w-full rounded-md border border-border bg-surface px-2 py-1"
          >
            {/* Vide par défaut, et vide reste une réponse : on ne met pas
                « fabricant » sur un PDF dont personne ne sait d'où il vient. */}
            <option value="">Inconnue</option>
            {SOURCES.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Fabricant du document">
          <input
            value={form.document_manufacturer ?? ""}
            onChange={(event) => set("document_manufacturer", event.target.value || null)}
            className="w-full rounded-md border border-border bg-surface px-2 py-1"
          />
        </Field>
        <div className="col-span-2 flex justify-end gap-2 pt-1">
          <button type="button" onClick={onDone} className="px-2 py-1 text-ink-subtle">
            Annuler
          </button>
          <button
            type="submit"
            disabled={update.isPending}
            className="rounded-md border border-border px-2 py-1 hover:bg-surface-raised disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>
      </form>
    </li>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-ink-subtle">{label}</span>
      {children}
    </label>
  );
}
